<template>
  <div class="web-orders">
    <div class="header">
      <h1>Web Orders</h1>
      <div class="controls">
        <router-link 
          to="/order-new-part" 
          class="create-order-button"
        >
          Create New Order
        </router-link>
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search orders..." 
          class="search-input"
        />
      </div>
    </div>
    
    <div v-if="isLoading" class="loading">Loading orders...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else>
      <!-- Table Container -->
      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>Order Number</th>
              <th>Description</th>
              <th>Customer</th>
              <th>Created By</th>
              <th>Create Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in orders" :key="order.id">
              <td>{{ order.OrderNumber }}</td>
              <td>{{ order.Description }}</td>
              <td>{{ order.Customer }}</td>
              <td>{{ order.UserName }}</td>
              <td>{{ formatDate(order.CreateDate) }}</td>
              <td>
                <span :class="['status-badge', getStatusClass(order.Status)]">
                  {{ order.Status }}
                </span>
              </td>
              <td>
                <button 
                  class="view-btn"
                  @click="viewOrderDetails(order)"
                >
                  View Details
                </button>
                <!-- Delete option disabled - orders cannot be deleted once submitted -->
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Controls -->
      <div class="pagination-container" v-if="pagination.totalPages > 1">
        <div class="pagination-info">
          <span>Showing {{ pagination.itemsPerPage * (pagination.currentPage - 1) + 1 }} to {{ Math.min(pagination.itemsPerPage * pagination.currentPage, pagination.totalItems) }} of {{ pagination.totalItems }} orders</span>
        </div>
        <div class="pagination-controls">
          <button 
            class="pagination-btn"
            :disabled="!pagination.hasPreviousPage"
            @click="goToPage(1)"
          >
            First
          </button>
          <button 
            class="pagination-btn"
            :disabled="!pagination.hasPreviousPage"
            @click="goToPage(pagination.currentPage - 1)"
          >
            Previous
          </button>
          
          <div class="page-numbers">
            <button 
              v-for="page in visiblePages" 
              :key="page"
              class="pagination-btn page-number"
              :class="{ active: page === pagination.currentPage }"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
          </div>
          
          <button 
            class="pagination-btn"
            :disabled="!pagination.hasNextPage"
            @click="goToPage(pagination.currentPage + 1)"
          >
            Next
          </button>
          <button 
            class="pagination-btn"
            :disabled="!pagination.hasNextPage"
            @click="goToPage(pagination.totalPages)"
          >
            Last
          </button>
        </div>
        <div class="items-per-page">
          <label for="itemsPerPage">Items per page:</label>
          <select 
            id="itemsPerPage"
            v-model="itemsPerPage" 
            @change="changeItemsPerPage"
            class="items-select"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'WebOrdersView',
  data() {
    return {
      orders: [],
      searchQuery: '',
      isLoading: true,
      error: null,
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false
      },
      itemsPerPage: 10,
      selectedCustomer: '',
      selectedStatus: '',
      customers: [],
      searchTimeout: null
    };
  },
  computed: {
    visiblePages() {
      const current = this.pagination.currentPage;
      const total = this.pagination.totalPages;
      const delta = 2; // Number of pages to show on each side of current page
      
      let start = Math.max(1, current - delta);
      let end = Math.min(total, current + delta);
      
      // Adjust if we're near the beginning or end
      if (current <= delta) {
        end = Math.min(total, 2 * delta + 1);
      }
      if (current >= total - delta) {
        start = Math.max(1, total - 2 * delta);
      }
      
      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  watch: {
    searchQuery() {
      // Debounce search to avoid too many API calls
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.currentPage = 1; // Reset to first page on search
        this.fetchOrders();
      }, 300);
    },
    selectedStatus() {
      this.pagination.currentPage = 1;
      this.fetchOrders();
    },
    selectedCustomer() {
      this.pagination.currentPage = 1;
      this.fetchOrders();
    }
  },
  methods: {
    async fetchOrders() {
      this.isLoading = true;
      try {
        const response = await axios.get('/weborders', {
          params: {
            searchTerm: this.searchQuery || undefined,
            customer: this.selectedCustomer || undefined,
            status: this.selectedStatus || undefined,
            page: this.pagination.currentPage,
            limit: this.itemsPerPage
          }
        });
        
        // Handle the new paginated response format
        if (response.data.data && response.data.pagination) {
          this.orders = response.data.data;
          this.pagination = response.data.pagination;
        } else {
          // Fallback for non-paginated response (shouldn't happen with new backend)
          this.orders = response.data;
          this.pagination = {
            currentPage: 1,
            totalPages: 1,
            totalItems: response.data.length,
            itemsPerPage: response.data.length,
            hasNextPage: false,
            hasPreviousPage: false
          };
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
        this.error = error.response?.data?.message || 'Failed to fetch orders';
      } finally {
        this.isLoading = false;
      }
    },
    async goToPage(page) {
      if (page >= 1 && page <= this.pagination.totalPages && page !== this.pagination.currentPage) {
        this.pagination.currentPage = page;
        await this.fetchOrders();
      }
    },
    async changeItemsPerPage() {
      this.pagination.currentPage = 1;
      await this.fetchOrders();
    },
    async fetchCustomers() {
      if (!this.isAdminOrManager) {
        return;
      }
      try {
        const response = await axios.get('/customers');
        this.customers = response.data;
      } catch (error) {
        console.error('Error fetching customers:', error);
        this.error = error.response?.data?.message || 'Failed to fetch customers';
      }
    },
    async updateOrderStatus(orderId, newStatus) {
      try {
        await axios.put(`/weborders/${orderId}/status`, {
          status: newStatus
        });
        await this.fetchOrders();
      } catch (error) {
        console.error('Error updating order status:', error);
        this.error = error.response?.data?.message || 'Failed to update order status';
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getStatusClass(status) {
      switch (status?.toLowerCase()) {
        case 'pending':
          return 'status-pending';
        case 'in progress':
          return 'status-progress';
        case 'completed':
          return 'status-completed';
        case 'cancelled':
          return 'status-cancelled';
        default:
          return 'status-default';
      }
    },
    isKitOrder(order) {
      return order.IsKit === true
        || order.IsKit === 'true'
        || order.IsKit === 1
        || (order.OrderNumber && String(order.OrderNumber).includes(' - KIT'));
    },
    viewOrderDetails(order) {
      if (this.isKitOrder(order) && order.KitID != null) {
        this.$router.push(`/kits/${order.KitID}/line-items`);
        return;
      }
      this.$router.push(`/weborderlineitems/${order.id}`);
    },
  },
  mounted() {
    this.fetchOrders();
    this.fetchCustomers();
  }
};
</script>

<style scoped>
.web-orders {
  padding: 2rem;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

h1 {
  color: var(--text-primary);
  font-size: 1.8rem;
  font-weight: 600;
}

.controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.create-order-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.2s ease;
  margin-right: 1rem;
}

.create-order-button:hover {
  background-color: var(--primary-hover);
}

.search-input {
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 300px;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.table-container {
  background: var(--background);
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.data-table th {
  background-color: var(--background-alt);
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
}

.data-table td {
  font-size: 0.875rem;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-pending {
  background-color: #fef3c7;
  color: #92400e;
}

.status-progress {
  background-color: #dbeafe;
  color: #1e40af;
}

.status-completed {
  background-color: #dcfce7;
  color: #166534;
}

.status-cancelled {
  background-color: #fee2e2;
  color: #991b1b;
}

.status-default {
  background-color: #f3f4f6;
  color: #374151;
}

.view-btn {
  background-color: var(--primary-color);
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.view-btn:hover {
  background-color: var(--primary-hover);
}

.loading {
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
}

.error {
  background-color: #fee2e2;
  color: #991b1b;
  padding: 1rem;
  border-radius: 0.5rem;
  margin: 1rem 0;
  text-align: center;
  border: 1px solid #fecaca;
}

/* Pagination Styles */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding: 1rem;
  background: var(--background);
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.pagination-info {
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-btn {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  background: white;
  color: var(--text-primary);
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  min-width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover:not(:disabled) {
  background-color: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f9fafb;
}

.pagination-btn.active {
  background-color: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
}

.page-number {
  min-width: 2.5rem;
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.items-select {
  padding: 0.375rem 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: white;
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
}

.items-select:focus {
  outline: none;
  border-color: var(--primary-color);
}

@media (max-width: 768px) {
  .web-orders {
    padding: 1rem;
  }

  .header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .search-input {
    width: 100%;
  }

  .data-table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }

  .pagination-container {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: center;
    flex-wrap: wrap;
  }

  .pagination-info,
  .items-per-page {
    text-align: center;
  }

  .page-numbers {
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style> 